// Client wrapper for the super-admin read-only SQL console. All enforcement
// (super_admin gate, statement allow-list, read-only execution, timeout, row
// cap, audit) happens server-side in the `admin-sql-console` Edge Function —
// this just invokes it with the caller's session JWT and shapes the result for
// the grid. Results are never persisted; they live in component state only.

import { supabase } from "./supabase";

export type SqlConsoleResult = {
  ok: boolean;
  rows: Record<string, unknown>[];
  columns: string[];
  rowCount: number;
  durationMs: number;
  truncated: boolean;
  explain: boolean;
  error?: string;
};

const EMPTY: Omit<SqlConsoleResult, "ok" | "error"> = {
  rows: [],
  columns: [],
  rowCount: 0,
  durationMs: 0,
  truncated: false,
  explain: false,
};

export async function runSqlConsole(sql: string): Promise<SqlConsoleResult> {
  const { data, error } = await supabase.functions.invoke("admin-sql-console", {
    body: { sql },
  });

  // Non-2xx (403 forbidden, 400 rejected) come back as an error whose `context`
  // is the raw Response — pull the human message out of it.
  if (error) {
    let message = error.message;
    try {
      const ctx = (error as { context?: { json?: () => Promise<unknown> } }).context;
      if (ctx?.json) {
        const body = (await ctx.json()) as { message?: string; error?: string };
        message = body.message || body.error || message;
      }
    } catch {
      /* keep the generic message */
    }
    return { ok: false, ...EMPTY, error: message };
  }

  const d = (data ?? {}) as Record<string, unknown>;
  // The function returns 200 with an `error` field for query failures
  // (syntax / timeout / blocked-by-read-only).
  if (d.error) {
    return {
      ok: false,
      ...EMPTY,
      durationMs: typeof d.duration_ms === "number" ? d.duration_ms : 0,
      error: (d.message as string) || (d.error as string),
    };
  }

  const rows = Array.isArray(d.rows) ? (d.rows as Record<string, unknown>[]) : [];
  const columns = rows.length > 0 ? Object.keys(rows[0]) : [];
  return {
    ok: true,
    rows,
    columns,
    rowCount: typeof d.row_count === "number" ? d.row_count : rows.length,
    durationMs: typeof d.duration_ms === "number" ? d.duration_ms : 0,
    truncated: d.truncated === true,
    explain: d.explain === true,
  };
}

export type ExampleQuery = { label: string; sql: string };

export const SQL_CONSOLE_EXAMPLES: ExampleQuery[] = [
  {
    label: "Signups last 7 days",
    sql: "select date_trunc('day', created_at)::date as day, count(*)\nfrom user_profiles\nwhere created_at >= now() - interval '7 days'\ngroup by 1 order by 1 desc",
  },
  {
    label: "Users by role",
    sql: "select role, count(*) as n\nfrom user_roles\ngroup by role order by n desc",
  },
  {
    label: "Open cases",
    sql: "select id, name, status, created_at\nfrom competitions\nwhere status in ('submitted','under_review','approved','open')\norder by created_at desc",
  },
  {
    label: "Companies + competition counts",
    sql: "select c.name, count(comp.id) as competitions\nfrom companies c\nleft join competitions comp on comp.company_id = c.id\ngroup by c.name order by competitions desc",
  },
];
